import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import { serviceApi } from '../services/serviceApi';
import { Service } from '../types/Service';
import { customerSegmentApi } from '../services/customerSegmentApi';
import { CustomerSegment } from '../types/CustomerSegment';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArchiveBoxIcon,
  ArrowPathIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  ChevronUpIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';

const ServicesManager: React.FC = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState<Service[]>([]);
  const [segments, setSegments] = useState<CustomerSegment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [archiveConfirm, setArchiveConfirm] = useState<Service | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    isGlobal: true,
    segmentIds: [] as number[]
  });

  useEffect(() => {
    loadServices();
    loadSegments();
  }, []);

  const loadServices = async () => {
    setLoading(true);
    try {
      const response = await serviceApi.getServices(true); // Include archived
      setServices(response.data || []);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const loadSegments = async () => {
    try {
      const response = await customerSegmentApi.getSegments(false);
      setSegments(response.data || []);
    } catch (err: any) {
      console.error('Failed to load segments:', err);
    }
  };

  const handleOpenModal = (service?: Service) => {
    if (service) {
      setEditingService(service);
      setFormData({
        name: service.name,
        code: service.code,
        description: service.description || '',
        isGlobal: service.isGlobal,
        segmentIds: service.segmentIds || []
      });
    } else {
      setEditingService(null);
      setFormData({
        name: '',
        code: '',
        description: '',
        isGlobal: true,
        segmentIds: []
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingService(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      if (editingService) {
        await serviceApi.updateService(editingService.id, formData);
        setSuccessMessage('Service updated successfully');
      } else {
        await serviceApi.createService(formData);
        setSuccessMessage('Service created successfully');
      }
      
      await loadServices();
      handleCloseModal();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save service');
    }
  };

  const handleArchive = async (service: Service) => {
    setArchiveConfirm(null);
    try {
      await serviceApi.archiveService(service.id);
      setSuccessMessage('Service archived successfully');
      await loadServices();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to archive service');
    }
  };

  const handleRestore = async (service: Service) => {
    try {
      await serviceApi.restoreService(service.id);
      setSuccessMessage('Service restored successfully');
      await loadServices();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to restore service');
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    
    const newServices = [...services];
    [newServices[index], newServices[index - 1]] = [newServices[index - 1], newServices[index]];
    
    const orders = newServices.map((svc, idx) => ({
      id: svc.id,
      orderIndex: idx + 1
    }));
    
    try {
      await serviceApi.reorderServices(orders);
      await loadServices();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reorder services');
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index === services.length - 1) return;
    
    const newServices = [...services];
    [newServices[index], newServices[index + 1]] = [newServices[index + 1], newServices[index]];
    
    const orders = newServices.map((svc, idx) => ({
      id: svc.id,
      orderIndex: idx + 1
    }));
    
    try {
      await serviceApi.reorderServices(orders);
      await loadServices();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reorder services');
    }
  };

  if (loading) {
    return (
      <Layout title="Services" subtitle="Manage organization services">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading services...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Services" subtitle="Manage services offered by your organization">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Services</h2>
            <p className="text-gray-600 mt-1">
              Create and manage services offered by your organization
            </p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Add Service</span>
          </button>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
            {successMessage}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Services Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Availability</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Leads</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {services.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                    No services found. Create your first service to get started.
                  </td>
                </tr>
              ) : (
                services.map((service, index) => (
                  <tr 
                    key={service.id} 
                    className={`${!service.isActive ? 'bg-gray-50 opacity-60' : ''} hover:bg-blue-50 cursor-pointer transition-colors`}
                    onClick={() => navigate(`/services/${service.id}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleMoveUp(index)}
                          disabled={index === 0}
                          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ChevronUpIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleMoveDown(index)}
                          disabled={index === services.length - 1}
                          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ChevronDownIcon className="h-4 w-4" />
                        </button>
                        <span className="text-sm text-gray-500 ml-2">{index + 1}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">{service.name}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-mono bg-gray-100 text-gray-700 rounded">
                        {service.code}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600 line-clamp-2">
                        {service.description || '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {service.isGlobal ? (
                        <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                          Global
                        </span>
                      ) : (
                        <div className="flex flex-col space-y-1">
                          <span className="text-xs text-purple-600 font-medium">Segment-Specific</span>
                          {service.segmentCodes && (
                            <span className="text-xs text-gray-500">{service.segmentCodes}</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                        {service.leadsCount || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {service.isActive ? (
                        <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end space-x-2">
                        {service.isActive ? (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenModal(service);
                              }}
                              className="text-blue-600 hover:text-blue-800"
                              title="Edit"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setArchiveConfirm(service);
                              }}
                              className="text-orange-600 hover:text-orange-800"
                              title="Archive"
                            >
                              <ArchiveBoxIcon className="h-5 w-5" />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRestore(service);
                            }}
                            className="text-green-600 hover:text-green-800"
                            title="Restore"
                          >
                            <ArrowPathIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingService ? 'Edit Service' : 'Create Service'}
                </h2>
                <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Visa Processing, University Admission"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono"
                    placeholder="e.g., VISA-PROC, UG-ADMISSION"
                    pattern="[A-Z0-9_-]+"
                    title="Only uppercase letters, numbers, dashes, and underscores"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Uppercase letters, numbers, dashes, underscores only</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Describe this service..."
                  />
                </div>

                {/* Segment Assignment */}
                <div className="space-y-3 border-t pt-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Service Availability
                  </label>
                  
                  {/* Global vs Segment-Specific */}
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={formData.isGlobal}
                        onChange={() => setFormData(prev => ({ ...prev, isGlobal: true, segmentIds: [] }))}
                        className="h-4 w-4 text-blue-600"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900">Global Service</span>
                        <p className="text-xs text-gray-500">Available for all customer segments</p>
                      </div>
                    </label>

                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={!formData.isGlobal}
                        onChange={() => setFormData(prev => ({ ...prev, isGlobal: false }))}
                        className="h-4 w-4 text-blue-600"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900">Segment-Specific Service</span>
                        <p className="text-xs text-gray-500">Only available to selected customer segments</p>
                      </div>
                    </label>
                  </div>

                  {/* Segment Selector (only if not global) */}
                  {!formData.isGlobal && (
                    <div className="ml-6 mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Assign to Segments <span className="text-red-500">*</span>
                      </label>
                      <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                        {segments.length === 0 ? (
                          <p className="text-sm text-gray-500">No customer segments available. Create segments first.</p>
                        ) : (
                          segments.map((segment) => (
                            <label key={segment.id} className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.segmentIds.includes(segment.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setFormData(prev => ({
                                      ...prev,
                                      segmentIds: [...prev.segmentIds, segment.id]
                                    }));
                                  } else {
                                    setFormData(prev => ({
                                      ...prev,
                                      segmentIds: prev.segmentIds.filter(id => id !== segment.id)
                                    }));
                                  }
                                }}
                                className="h-4 w-4 text-blue-600 rounded"
                              />
                              <div className="flex items-center space-x-2">
                                <div
                                  className="w-3 h-3 rounded-full"
                                  style={{ backgroundColor: segment.color }}
                                />
                                <span className="text-sm text-gray-900">{segment.name}</span>
                                <span className="text-xs text-gray-500 font-mono">({segment.code})</span>
                              </div>
                            </label>
                          ))
                        )}
                      </div>
                      {!formData.isGlobal && formData.segmentIds.length === 0 && (
                        <p className="text-xs text-red-600 mt-1">Please select at least one segment</p>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {editingService ? 'Update Service' : 'Create Service'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Archive Confirmation Modal */}
        {archiveConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center space-x-3 text-orange-600 mb-4">
                <ExclamationTriangleIcon className="h-6 w-6" />
                <h3 className="text-lg font-semibold">Archive Service?</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Are you sure you want to archive "<strong>{archiveConfirm.name}</strong>"?
              </p>
              {(archiveConfirm.leadsCount || 0) > 0 && (
                <p className="text-sm text-orange-600 mb-4">
                  ⚠️ This service has {archiveConfirm.leadsCount} assigned lead(s).
                </p>
              )}
              <p className="text-sm text-gray-500 mb-6">
                Archived services can be restored later. Existing lead assignments will be preserved.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setArchiveConfirm(null)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleArchive(archiveConfirm)}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                >
                  Archive Service
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ServicesManager;
